import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Palette, Save, Loader2, Lock, ShieldCheck } from "lucide-react";

export default function BrandingPage() {
  const navigate = useNavigate();
  const [b, setB] = useState({ logoUrl: "", accentColor: "#2563eb", showBadge: true });
  const [canEdit, setCanEdit] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/branding").then(({ data }) => {
      setCanEdit(data.canEdit);
      setCompanyName(data.companyName || "");
      setB({ logoUrl: data.branding.logoUrl || "", accentColor: data.branding.accentColor || "#2563eb", showBadge: data.branding.showBadge !== false });
    }).catch(() => toast.error("Chargement impossible")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/company/branding", b); toast.success("Identité de marque enregistrée ✅"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="branding-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center"><Palette className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Identité de marque</h1>
          <p className="text-sm text-muted-foreground">Logo et couleur appliqués à toutes les cartes de {companyName || "votre équipe"}.</p>
        </div>
      </div>

      {!canEdit && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 p-3 text-sm" data-testid="branding-locked">
          <Lock className="w-4 h-4" /> Seul un manager peut modifier l'identité de marque de l'équipe.
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-sm space-y-5">
        <div className="space-y-1.5">
          <Label className="text-muted-foreground">Logo (URL)</Label>
          <Input data-testid="branding-logo" value={b.logoUrl} disabled={!canEdit} onChange={(e) => setB({ ...b, logoUrl: e.target.value })} placeholder="https://…/logo.png" />
          {b.logoUrl && <img src={b.logoUrl} alt="logo" className="h-10 mt-2 object-contain" data-testid="branding-logo-preview" />}
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground">Couleur d'accent</Label>
          <div className="flex items-center gap-3">
            <input type="color" data-testid="branding-color" value={b.accentColor} disabled={!canEdit} onChange={(e) => setB({ ...b, accentColor: e.target.value })} className="h-11 w-16 rounded-lg border border-border bg-transparent cursor-pointer disabled:opacity-60" />
            <Input value={b.accentColor} disabled={!canEdit} onChange={(e) => setB({ ...b, accentColor: e.target.value })} className="max-w-[140px] font-mono" />
            <span className="h-11 flex-1 rounded-lg border border-border" style={{ background: b.accentColor }} />
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="branding-badge" checked={b.showBadge} disabled={!canEdit} onChange={(e) => setB({ ...b, showBadge: e.target.checked })} className="h-4 w-4 accent-primary" />
          <span className="text-sm">Afficher « Propulsé par Kallitag » sur les cartes publiques</span>
        </label>
        {canEdit && (
          <Button onClick={save} disabled={saving} data-testid="branding-save-button" className="w-full rounded-xl h-11">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer l'identité</>}
          </Button>
        )}
      </div>

      <Button variant="outline" onClick={() => navigate("/ma-carte")} className="mt-4 rounded-xl w-full" data-testid="branding-back-card">
        <ShieldCheck className="w-4 h-4 mr-2" /> Voir ma carte
      </Button>
    </div>
  );
}
